import { PutObjectCommand, S3Client } from "@aws-sdk/client-s3";
import * as Sentry from "@sentry/browser";
import {
  API_URL,
  ASSESSMENT_STATUSES,
  AWS_CONFIG,
  DL_MODELS,
  S3_BUCKET,
} from "../movenet/params";
import { SendExceptionEmail } from "../services/ExercisesService";
import { getPatientAge } from "../utils/sharedFunction";
import { config } from "../vision-algo/env_config";
import { AssessmentStates, GaitAudios } from "./assessment_states";
import { AudioPlayer } from "./audio-player";
import { calculateFallRisk } from "./gait-util";
import { MultiPoseUtility } from "./multipose_utility";
import { SentryLogging } from "./sending_sentry_logs";
import { SinglePoseUtil } from "./single_pose_utility";
import { GestureDetector } from "./start_gesture_detector";
import { PatientSelection } from "./patient_selection";
import { StepDetector } from "./step-detector";
import {
  startiOSVoiceListening,
  stopiOSVoiceListening,
} from "../utils/mobile-bridge";
import shoePrintSrc from "../assets/images/shoe_print.png";

const FEET_BOX_LENGTH = 120;
const cv = window.cv;
const s3Client = new S3Client(AWS_CONFIG);

/**
 * Represents foot data in the Bird's Eye View coordinate system.
 * Contains timestamps, foot positions, and slope data (if used).
 */
export class FeetData {
  /**
   * @param {object} left_ankle_bev - The x,y point for the left ankle in Bird's Eye View.
   * @param {object} right_ankle_bev - The x,y point for the right ankle in Bird's Eye View.
   */
  constructor(left_ankle_bev, right_ankle_bev) {
    this.timestamp = Date.now();
    this.elapsedTime = 0;
    this.left_foot_bev = left_ankle_bev;
    this.right_foot_bev = right_ankle_bev;
    this.right_foot_bev_slope = 0;
    this.left_foot_bev_slope = 0;
  }
}

/**
 * The Gait class orchestrates the logic for a walking or gait assessment.
 * It integrates hand-gesture detection, foot tracking, distance calibration,
 * start/stop line detection, and generation of final results.
 */
export class Gait {
  /**
   * Constructs a new Gait instance.
   * @param {object} calibration - Calibration object responsible for computing Bird's Eye View transformations.
   * @param {object} camera - An object that wraps around video/canvas functionalities.
   * @param {function} switch_to_single_pose - Callback that switches the pose detection model to BlazePose (single-pose).
   * @param {function} reset_pose_estimator - Resets the pose estimator to capture full-sized frames.
   */
  constructor(
    calibration,
    camera,
    switch_to_single_pose,
    reset_pose_estimator
  ) {
    this.switch_to_single_pose_callback = switch_to_single_pose;
    this.reset_pose_estimator = reset_pose_estimator;
    this.states = [
      "Calibration start",
      "Calibration complete",
      "Ready to start test",
      "Start gesture detected",
      "Test in progress",
      "Test complete",
    ];
    this.current_state = AssessmentStates.assessment_started;
    this.calibration = calibration;
    this.gesture_detector = new GestureDetector();
    this.camera = camera;
    this.left_boundary = 0;
    this.latest_poses = null;
    this.right_boundary = 0;
    this.shoulder_differernce = 0;
    this.patient_id = -1;
    this.therapist_id = -1;
    this.frames_with_no_patient = 0;
    this.start_time = 0;
    this.end_time = 0;
    this.calibration_error_start_time = 0;
    this.calibration_error_time = 0;
    this.total_calibration_error_time = 0;
    this.calibration_error_occured = false;
    this.two_second_behind_the_point_start = 0;
    this.two_second_behind_the_point_end = 0;
    this.show_raise_hand_modal = true;
    this.first_frame = true;
    this.audio_player = new AudioPlayer();
    this.display_start_assessment_modal = true;
    this.pathwayBlocked = true;
    this.pathwayCalibrated = true;
    this.patient_in_box_counter = 0;
    this.ankle_locations_bev = [
      [
        "Timestamp",
        "left_foot_bev_x",
        "left_foot_bev_y",
        "right_foot_bev_x",
        "right_foot_bev_y",
        "left_foot_cv_x",
        "left_foot_cv_y",
        "right_foot_cv_x",
        "right_foot_cv_y",
        "left_ankle_cv_x",
        "left_ankle_cv_y",
        "right_ankle_cv_x",
        "right_ankle_cv_y",
        "left_foot_score",
        "right_foot_score",
        "left_ankle_score",
        "right_ankle_score",
      ],
    ];
    this.step_detector = new StepDetector();

    // Offscreen canvas for capturing frames to pass to the depth model
    let canvas = document.createElement("canvas");
    canvas.width = 1080;
    canvas.height = 1920;
    this.offscreen_canvas = canvas;
    this.offscreen_canvas_ctx = this.offscreen_canvas.getContext("2d", {
      willReadFrequently: true,
    });
    this.canvas = document.getElementById("output");
    this.ctx = this.canvas.getContext("2d");
    this.side_margin = 0.4;

    this.feet_locations = [];
    this.feet_locations_cam = [];
    this.LEFT_FOOT_IDX = 31;
    this.RIGHT_FOOT_IDX = 32;
    this.LEFT_ANKLE_IDX = 27;
    this.RIGHT_ANKLE_IDX = 28;
    this.LEFT_SHOULDER_IDX = 11;
    this.RIGHT_SHOULDER_IDX = 12;
    let videoWidth = 1080;
    let videoHeight = 1920;
    this.options = {
      sx: 0,
      sy: 0,
      sw: videoWidth,
      sh: videoHeight,
    };

    this.are_you_there_flag = true;
    this.are_you_there_timer = 0;
    this.two_seconds_timer_flag = true;
    this.hand_gesture_detected = false;
    this.cropped_image_length = 0;
    this.cropped_image_width = 0;
    this.left_shoulder_bound = 0;
    this.right_shoulder_bound = 0;
    this.shoulder_diff = 0;
    this.ready_to_start_test_flag = true;
    this.prev_index = 0;
    this.left_shoulder = 0;
    this.right_shoulder = 0;
    this.two_second_start_time = 0;
    this.is_everyone_behind_the_point_counter = 0;
    this.crossed_finish_line = false;
    this.callibration_audio_completion = false;
    this.start_hand_gesture_detection = false;
    this.start_hand_gesture_detection_counter = 0;
    this.shown_persistent_icon = false;
    this.finish_line_crossed = false;
    this.start_line_crossed = false;
    this.start_line_extended_crossed = false;
    this.prev_left_ankle_value = 0;
    this.prev_right_ankle_value = 0;
    this.prev_left_shoulder_value = { x: null, y: null };
    this.prev_right_shoulder_value = { x: null, y: null };
    this.flickerStatus = false;
    this.inactive_for_60_seconds = false;

    // Sentry / Logging
    let patientServerID = localStorage.getItem("patientServerID")
      ? localStorage.getItem("patientServerID")
      : "000";
    let userID = localStorage.getItem("UserID")
      ? localStorage.getItem("UserID")
      : "000";
    let businessCode = localStorage.getItem("BusinessCode")
      ? localStorage.getItem("BusinessCode")
      : "000";
    Sentry.setTag("UserID", userID);
    Sentry.setTag("Patient Server ID", patientServerID);
    Sentry.setTag("Business Code", businessCode);
    this.sentry_prefix = `Gait_` + new Date().toISOString();
    ASSESSMENT_STATUSES.sentry_message = this.sentry_prefix;
    ASSESSMENT_STATUSES.are_you_there = false;
    ASSESSMENT_STATUSES.incomplete_test = false;
    ASSESSMENT_STATUSES.assessment_Completed = false;
    ASSESSMENT_STATUSES.assessment_successful_completion = false;
    this.sentry_start_time = 0;

    // Reading are-you-there-timer & converting it to milliseconds
    this.are_you_there_modal_timer =
      config["are-you-there-timer-in-seconds"] * 1000;
    this.patient_feet_box_x = [];
    this.patient_feet_box_y = [];

    // Load shoe print image for bounding box
    this.shoe_print_img = new Image();
    this.shoe_print_img.src = shoePrintSrc;
    this.shoe_print_loaded = false;
    this.shoe_print_img.onload = () => {
      this.shoe_print_loaded = true;
    };

    this.is_captured = null;
    this.apicall = null;
    this.is_uploaded = null;
    this.audio_play = null;
    this.is_calibrated = null;
    this.isPatientNearStopLine = null;
    this.is_depth_calculated = null;
    this.is_api_called = null;
    this.current_y = 0;
    this.old_y = 0;
    this.pathBlockedCount = null;
    this.enabled_voice_listener = false;
    this.voice_command_received = false;
    // Remote UP/DOWN patient selection: the participant the therapist selects before the start
    this.patient_selection = new PatientSelection();
    window.addEventListener("voice-command", (event) => {
      const command = event.detail.command;
      console.log("🎤 [Gait] Received voice command:", command);

      // Example: Trigger something based on the command
      if (command === "START") {
        console.log("Voice Triggered START command");
        if (this.enabled_voice_listener && !this.voice_command_received) {
          this.voice_command_received = true;
          this.enabled_voice_listener = false;
          stopiOSVoiceListening();
        }
      }
    });
  }

  /**
   * Main update function called on each animation frame. Manages the current state machine,
   * draws lines, checks for path obstruction, and handles logic for start/finish detection.
   * @param {Array} poses - The array of poses detected in the current frame.
   */
  async update(poses) {
    this.latest_poses = poses;
    // The participant the therapist selected with the remote, while it can still be changed.
    // patient_id also makes movenet draw this person's skeleton green.
    if (this.is_patient_selection_open()) {
      const patient = this.patient_selection.update(poses);
      this.patient_id = patient ? patient.id : -1;
    }
    // Draw lines if calibration is completed
    if (this.calibration.complete()) {
      if (this.is_start_line_crossed()) {
        this.camera.draw_line(this.calibration.start_line_cv, "green");
      } else {
        this.camera.draw_line(this.calibration.start_line_cv, "red");
      }

      if (this.is_finish_line_crossed()) {
        this.camera.draw_line(this.calibration.stop_line_cv, "green");
      } else {
        this.camera.draw_line(this.calibration.stop_line_cv, "red");
      }
    }

    // If the test is complete or in an interrupted state, exit early
    if (
      this.current_state === AssessmentStates.test_complete ||
      ASSESSMENT_STATUSES.calibration_error_state ||
      this.current_state === AssessmentStates.assessment_interrupted ||
      ASSESSMENT_STATUSES.exit_assessment_modal
    ) {
      // Stop voice listening on any termination condition
      if (this.enabled_voice_listener) {
        console.log(
          "🔊 Stopping voice listening due to assessment termination"
        );
        stopiOSVoiceListening();
        this.enabled_voice_listener = false;
      }
      if (
        ASSESSMENT_STATUSES.calibration_error_state &&
        this.current_state === AssessmentStates.test_in_progress
      ) {
        this.calibration_error_occured = true;
        this.calibration_error_time =
          Date.now() - this.calibration_error_start_time;
      }

      this.are_you_there_flag = true;
      this.are_you_there_timer = 0;
      return;
    }

    // Check if no poses are detected -> triggers an "Are you there?" modal if idle for too long
    if (!poses || poses.length === 0) {
      if (this.are_you_there_flag) {
        this.are_you_there_flag = false;
        this.are_you_there_timer = Date.now();
      }
      let time_now = Date.now();
      const time_since_screen_inactive = time_now - this.are_you_there_timer;
      if (
        time_since_screen_inactive >= 60000 &&
        !this.inactive_for_60_seconds
      ) {
        this.inactive_for_60_seconds = true;
        this.append_sentry_message(
          "No pose/activity detected for more than 60 seconds."
        );
      }
      if (time_since_screen_inactive >= this.are_you_there_modal_timer) {
        this.are_you_there_flag = true;
        this.are_you_there_timer = 0;
        document.getElementById("hdnAreYouTherePopup").click();
      }
    } else {
      this.are_you_there_flag = true;
      this.are_you_there_timer = 0;
    }

    if (document.readyState !== "complete") {
      return;
    }

    // Log the first frame, and optionally play an audio
    if (this.first_frame) {
      this.append_sentry_message("Assessment Started");
      if (DL_MODELS.MessageForSentry === "") {
        this.append_sentry_message("DL_Model already loaded");
      } else {
        this.append_sentry_message(DL_MODELS.MessageForSentry);
        DL_MODELS.MessageForSentry = "";
      }
      this.append_sentry_message(
        "Screen Resolution: " + this.canvas.width + "x" + this.canvas.height
      );
      this.first_frame = false;
      this.camera.flip_horizontal();
      this.audio_player.play(GaitAudios.stand_in_starting_area);

      if (ASSESSMENT_STATUSES.debug_calibration) {
        ASSESSMENT_STATUSES.debug_calibration = false;
      }
    }

    if (ASSESSMENT_STATUSES.debug_calibration) {
      this.camera.video.pause();
    }

    try {
      switch (this.current_state) {
        // --------------------------- 1) ASSESSMENT STARTED ------------------------------
        case AssessmentStates.assessment_started:
          if (
            this.audio_player.is_available() &&
            !this.callibration_audio_completion
          ) {
            this.append_sentry_message("Calibration Started");
            this.callibration_audio_completion = true;
          }
          this.calibration.on_complete_callback();
          this.get_start_extended_points();
          this.draw_PatientFeetBox(poses);

          // Buffer variable -> 30% above & below the box's middle point
          let patient_inner_buffer = Math.abs(
            (this.patient_feet_box_y[0] - this.patient_feet_box_y[1]) * 0.2
          );

          // Check if someone is in the box
          if (
            MultiPoseUtility.is_someone_in_box(
              this.filter_poses_with_confident_ankles(poses),
              this.patient_feet_box_x,
              [
                this.patient_feet_box_y[0] + patient_inner_buffer,
                this.patient_feet_box_y[1] - patient_inner_buffer,
              ],
              this.camera
            )
          ) {
            this.patient_in_box_counter++;
            if (this.patient_in_box_counter >= 5) {
              if (!this.is_depth_calculated) {
                this.current_state = AssessmentStates.pathway_calibration_start;
              } else {
                this.current_state = AssessmentStates.ready_to_start_test;
              }
              this.gesture_detector._screen_color_change();
              this.show_raise_hand_modal = true;
              document.getElementById("hdnPersonDetectedModalPopup").click();
              this.start_hand_gesture_detection = false;
              this.shown_persistent_icon = false;
              console.log("Everyone is behind start point!");
              this.append_sentry_message("Everyone is behind start point!");
              this.two_second_start_time = Date.now();
            }
          } else {
            this.patient_in_box_counter = 0;
          }
          break;

        // --------------------------- 2) PATHWAY CALIBRATION START -----------------------
        case AssessmentStates.pathway_calibration_start:
          let elapsed_time1 = Date.now() - this.two_second_start_time;
          this.pathwayBlocked = true;
          this.draw_PathClearanceBox();
          this.draw_PatientFeetBox(poses);

          if (elapsed_time1 <= 3500) {
            this.gesture_detector._screen_color_change();
            return;
          }

          console.warn("AssessmentStates.pathway_calibration_start: ");
          let patient_in_box1 = MultiPoseUtility.is_someone_in_box(
            this.filter_poses_with_confident_ankles(poses),
            this.patient_feet_box_x,
            this.patient_feet_box_y,
            this.camera
          );

          if (!patient_in_box1 && this.audio_player.is_available()) {
            this.handleEveryoneBehindPoint();
          }

          if (patient_in_box1 && this.audio_player.is_available()) {
            this.current_state =
              AssessmentStates.pathway_calibration_audio_completed;
            this.audio_player.play(GaitAudios.pathway_calibration_start);
            this.two_second_start_time = Date.now();
          }

          break;

        // --------------------------- 3) PATHWAY CALIBRATION AUDIO COMPLETED -------------
        case AssessmentStates.pathway_calibration_audio_completed:
          this.draw_PathClearanceBox();
          this.draw_PatientFeetBox(poses);

          // Check if the path is clear
          let is_path_clear = this.path_checking(poses);

          // Upload scene screenshot and call external API if path is clear
          if (!this.is_api_called && is_path_clear) {
            this.uploadImageToS3AndCallAPI();
            this.is_api_called = true;
          }

          if (this.pathwayCalibrated) {
            const event = new CustomEvent("dontwalkmodal", {
              detail: this.calibration.percentDepthData,
            });
            document.getElementById("hdnGaitDontWalk").dispatchEvent(event);
          }

          if (!this.audio_player.is_available()) return;

          let patient_in_box2 = MultiPoseUtility.is_someone_in_box(
            this.filter_poses_with_confident_ankles(poses),
            this.patient_feet_box_x,
            this.patient_feet_box_y,
            this.camera
          );

          // If patient left the box prematurely, revert back
          if (!patient_in_box2 && this.audio_player.is_available()) {
            this.handleEveryoneBehindPoint();
          }

          if (!this.audio_player.is_available()) return;

          // Path not clear -> path blocked error
          if (!is_path_clear) {
            console.warn("Pathway blocked GaitPathwayBlocked : ");
            if (!this.pathBlockedCount) {
              this.pathBlockedCount = 0;
            }
            this.pathBlockedCount++;

            if (this.pathBlockedCount > 3) {
              // Stop voice listening on pathway blocked error
              if (this.enabled_voice_listener) {
                console.log(
                  "🔊 Stopping voice listening due to pathway blocked"
                );
                stopiOSVoiceListening();
                this.enabled_voice_listener = false;
              }
              this.current_state = AssessmentStates.test_complete;
              document.getElementById("hdnRemoveGaitDontWalk")?.click();
              document.getElementById("hdnAssessementErrorPopup").click();
              ASSESSMENT_STATUSES.assessment_Completed = true;
              localStorage.removeItem("modalDisplayed");
              this.audio_player.play(GaitAudios.assessment_interrupted);
              console.log("Assessment ERROR occured.");
              this.append_sentry_message(
                "Assessment ERROR occured - Pathway has been blocked."
              );
              return;
            }

            if (this.pathwayBlocked) {
              this.pathwayBlocked = false;
              document.getElementById("hdnRemoveGaitDontWalk")?.click();
              document.getElementById("hdnGaitPathwayBlocked").click();
              if (this.audio_player.is_available()) {
                this.audio_player.play(GaitAudios.pathway_blocked);
              }
              this.current_state = AssessmentStates.pathway_calibration_start;
              this.two_second_start_time = Date.now();
            }

            // Path is clear & depth data is fetched -> complete calibration
          } else if (this.calibration.depthData) {
            if (this.audio_player.is_available()) {
              if (this.pathwayCalibrated) {
                this.pathwayCalibrated = false;
                setTimeout(() => {
                  this.audio_player.play(GaitAudios.calibrated);
                  document
                    .getElementById("hdnGaitCalibrationComplete")
                    ?.click();
                  this.is_depth_calculated = true;

                  setTimeout(() => {
                    this.current_state = AssessmentStates.ready_to_start_test;
                  }, 2000);
                }, 3000);
              }
            }
          }
          break;

        // --------------------------- 4) READY TO START TEST -----------------------------
        case AssessmentStates.ready_to_start_test:
          this.draw_PatientFeetBox(poses);
          this.pathwayBlocked = true;
          this.pathwayCalibrated = true;
          let elapsed_time = Date.now() - this.two_second_start_time;
          // With a remote selection, only the selected person counts; otherwise anyone in the box
          let patient_in_box = MultiPoseUtility.is_someone_in_box(
            this.filter_poses_with_confident_ankles(
              this.patient_selection.start_check_poses(poses)
            ),
            this.patient_feet_box_x,
            this.patient_feet_box_y,
            this.camera
          );

          if (elapsed_time <= 2500) {
            console.log("we are in 2 second buffer");
            this.is_everyone_behind_the_point_counter = 0;
            return;
          }

          if (!patient_in_box && this.audio_player.is_available()) {
            this.handleEveryoneBehindPoint();
          }

          // Not once the remote's START was accepted (it may come before the prompt)
          if (
            this.show_raise_hand_modal &&
            this.audio_player.is_available() &&
            !this.voice_command_received
          ) {
            // Prompt user to raise arm — reset gesture detector to clear any stale
            // Shoulder_time/Direction from prior states, since detect_start_gesture
            // now runs immediately (no counter delay)
            this.gesture_detector.reset();
            this.show_raise_hand_modal = false;
            this.audio_player.play(GaitAudios.raise_arm_to_start_audio);
            if (this.enabled_voice_listener) {
              stopiOSVoiceListening();
              this.enabled_voice_listener = false;
              // setTimeout(() => {
              //   this.audio_player.play(GaitAudios.raise_arm_to_start_audio);
              // }, 300);
            }
            // else {
            //   this.audio_player.play(GaitAudios.raise_arm_to_start_audio);
            // }
            this.start_hand_gesture_detection_counter = 0;
            this.append_sentry_message("Hand Raised Modal is displayed.");
            document.getElementById("hdnRaiseArmModalPopup").click();
          }

          // Record initial ankle location
          if (this.ready_to_start_test_flag) {
            this.gesture_detector.record_ankle_location(poses);
            this.ready_to_start_test_flag = false;
          }

          // Detect hand raise every frame (not gated by audio) — matches sit-stand pattern
          if (patient_in_box) {
            this.gesture_detector.detect_start_gesture(poses);

            // Voice listener gated by is_available() to avoid killing audio on iOS
            if (
              !this.enabled_voice_listener &&
              !this.voice_command_received &&
              this.audio_player.is_available()
            ) {
              console.log("🔊 Start of voice listening...");
              setTimeout(() => {
                startiOSVoiceListening();
              }, 250);
              this.enabled_voice_listener = true;
            }

            // Show persistent icon once audio finishes (replaces counter-based approach)
            if (!this.shown_persistent_icon && this.audio_player.is_available()) {
              this.shown_persistent_icon = true;
              document.getElementById("hdnRaiseArmStopModalPopup")?.click();
            }

            if (
              this.gesture_detector.start_gesture_issued() ||
              this.voice_command_received
            ) {
              if (!this.audio_player.is_available()) this.audio_player.stop();
              if (this.enabled_voice_listener) {
                console.log("🔊 Stopping voice listening...");
                stopiOSVoiceListening();
                this.enabled_voice_listener = false;
              }
              document.getElementById("hdnHideHandRaisePersistentIcon")?.click();
              this.hand_gesture_detected = true;
              console.log("Start gesture issued!");
              this.append_sentry_message("Start (Hand raised) gesture issued!");
              this.append_sentry_message(
                "Number of persons in the frame while gesture detection: " +
                  poses.length.toString()
              );
            }
          }

          // If using depth -> define start/stop lines in calibration & switch states
          if (this.hand_gesture_detected && this.calibration.depthData) {
            this.calibration.determine_start_stop_points();
            this.calibration.start_point = this.calibration.get_warped_point(
              this.calibration.start_point_cv
            );
            this.calibration.stop_point = this.calibration.get_warped_point(
              this.calibration.stop_point_cv
            );
            this.current_state = AssessmentStates.start_gesture_detected;

            // Reset pose detection to uncropped, then switch to single-pose
            this.reset_pose_estimator();
            this.switch_to_blazepose(poses);
            this.gesture_detector.reset();
          }
          break;

        // --------------------------- 5) START GESTURE DETECTED --------------------------
        case AssessmentStates.start_gesture_detected:
          if (poses.length > 1) {
            // Wait if multiple poses are detected while switching to BlazePose
            break;
          }
          if (this.display_start_assessment_modal) {
            this.audio_player.play(GaitAudios.assessment_go_audio);
            document.getElementById("hdnCountDownGaitCompletePopup").click();
            this.display_start_assessment_modal = false;
          }

          if (!this.flickerStatus) this.flickerDetection(poses);

          // Check if a random bystander is crossing behind the patient
          if (this.bird_eye_view_patient_crossing(poses)) {
            // Stop voice listening on patient crossing error
            if (this.enabled_voice_listener) {
              console.log(
                "🔊 Stopping voice listening due to patient crossing"
              );
              stopiOSVoiceListening();
              this.enabled_voice_listener = false;
            }
            this.current_state = AssessmentStates.test_complete;
            document.getElementById("hdnAssessementErrorPopup").click();
            ASSESSMENT_STATUSES.assessment_Completed = true;
            localStorage.removeItem("modalDisplayed");
            this.audio_player.play(GaitAudios.assessment_interrupted);
            console.log("Assessment ERROR occured.");
            this.append_sentry_message(
              "Assessment ERROR occured - Someone has crossed the Patient during assessment."
            );
            return;
          }

          // If the patient crosses the start line, move to "Test in progress"
          if (
            SinglePoseUtil.has_someone_crossed_start_line(
              poses,
              this.calibration
            ) &&
            poses.length
          ) {
            this.start_line_crossed = true;
            this.setting_patient_region(poses);
            this.current_state = AssessmentStates.test_in_progress;
            console.log("Start line crossed!");
            this.append_sentry_message(
              "assessment started - Start line crossed!"
            );
            this.start_time = Date.now();
            this.calibration_error_start_time = Date.now();
          }
          this.frames_with_no_patient = 0;
          break;

        // --------------------------- 6) TEST IN PROGRESS --------------------------------
        case AssessmentStates.test_in_progress:
          this.calibration_error_start_time = Date.now();
          if (this.calibration_error_occured) {
            this.calibration_error_occured = false;
            this.total_calibration_error_time =
              this.total_calibration_error_time + this.calibration_error_time;
          }
          if (!this.flickerStatus) this.flickerDetection(poses);

          // Check if the patient is near the finish line
          this.patient_near_stopline(poses);

          // Detect if a random crossing behind the patient
          if (
            this.bird_eye_view_patient_crossing(poses) &&
            !this.isPatientNearStopLine
          ) {
            // Error out if not near the stop line
            // Stop voice listening on stop line error
            if (this.enabled_voice_listener) {
              console.log("🔊 Stopping voice listening due to stop line error");
              stopiOSVoiceListening();
              this.enabled_voice_listener = false;
            }
            this.current_state = AssessmentStates.test_complete;
            document.getElementById("hdnAssessementErrorPopup").click();
            ASSESSMENT_STATUSES.assessment_Completed = true;
            localStorage.removeItem("modalDisplayed");
            this.audio_player.play(GaitAudios.assessment_interrupted);
            console.log("Assessment ERROR occured..");
            this.append_sentry_message(
              "Assessment ERROR occured - Someone has crossed the Patient during assessment.."
            );
            return;
          }

          // Record ankle positions for the entire test
          this.record_ankle_locations_BEV(poses);

          // Check if finishing line is crossed
          this.crossed_finish_line =
            SinglePoseUtil.has_someone_crossed_finish_line(
              poses,
              this.calibration
            ) && poses.length;

          if (
            (this.bird_eye_view_patient_crossing(poses) ||
              this.second_person_crossing_detection(poses)) &&
            this.isPatientNearStopLine
          ) {
            // If random crossing but the patient is near the stop line, consider it completed
            this.crossed_finish_line = true;
          }

          // If finish line is crossed, finalize the assessment
          if (this.crossed_finish_line && this.audio_player.is_available()) {
            this.finish_line_crossed = true;
            let feet_data = `${
              this.sentry_prefix
            } Feet location: ${JSON.stringify(this.feet_locations)}`;

            Sentry.captureMessage(
              "This message contains the Feet Location data.",
              {
                extra: {
                  full_message: feet_data,
                },
              }
            );
            this.voice_command_received = false; // reset voice command for the next assessment, or when discarding
            this.audio_player.play(GaitAudios.assessment_complete);
            this.end_time = Date.now();
            this.current_state = AssessmentStates.test_complete;
            console.log("Finish line crossed!");
            ASSESSMENT_STATUSES.assessment_Completed = true;
            ASSESSMENT_STATUSES.assessment_successful_completion = true;

            // Basic calculation for gait speed
            let distance = 10 * 0.3048; // 10 feet in meters
            let gait_time =
              (this.end_time -
                this.start_time -
                this.total_calibration_error_time) /
              1000; // in seconds

            let gait_speed = (distance / gait_time).toFixed(2); // m/s
            console.log("Gait speed: ", gait_speed, "m/s");

            let response = this.build_gait_response(
              gait_speed,
              distance,
              gait_time
            );

            console.log(response);
            this.append_sentry_message(
              "assessment completed - Finish line crossed!"
            );
            this.append_sentry_message(
              "Assessment response:" + JSON.stringify(response)
            );

            // Provide the response to the host page
            if (window["AssessmentPageReference"]) {
              window["AssessmentPageReference"].CompleteAssessment(response);
            } else {
              console.log("window['AssessmentPageReference'] not found");
            }
          } else if (
            this.second_person_crossing_detection(poses) &&
            !this.isPatientNearStopLine
          ) {
            // If someone crosses behind the patient prematurely
            console.log(
              "someone might have crossed the patient",
              this.frames_with_no_patient
            );
            this.frames_with_no_patient++;
            if (this.frames_with_no_patient >= 1) {
              // Stop voice listening when patient leaves frame
              if (this.enabled_voice_listener) {
                console.log(
                  "🔊 Stopping voice listening due to patient leaving frame"
                );
                stopiOSVoiceListening();
                this.enabled_voice_listener = false;
              }
              this.current_state = AssessmentStates.test_complete;
              document.getElementById("hdnAssessementErrorPopup").click();
              ASSESSMENT_STATUSES.assessment_Completed = true;
              localStorage.removeItem("modalDisplayed");
              this.audio_player.play(GaitAudios.assessment_interrupted);
              console.log("Assessment ERROR occured...");
              this.append_sentry_message(
                "Assessment ERROR occured - Someone has crossed the Patient during assessment..."
              );
              this.frames_with_no_patient = 0;
            }
          } else {
            // Reset if we didn't detect crossing
            this.frames_with_no_patient = 0;
          }
          break;

        default:
          break;
      }
    } catch (error) {
      console.log(error);
    }

    if (ASSESSMENT_STATUSES.debug_calibration) {
      this.camera.video.play();
    }
  }

  /**
   * True while the patient can be changed with the remote: from when the start area is known
   * until the start is accepted.
   * @returns {boolean}
   */
  is_patient_selection_open() {
    return (
      this.shouldUseCroppedFrames() &&
      !this.voice_command_received &&
      !ASSESSMENT_STATUSES.exit_assessment_modal &&
      !ASSESSMENT_STATUSES.calibration_error_state
    );
  }

  /**
   * Patient selection from outside the frame loop (BLE remote UP/DOWN): moves the patient to the
   * next detected person on screen. They still have to be in the start area for the test to start.
   * @param {number} direction - +1 for the next person to the right, -1 for the next to the left.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if the selection changed.
   */
  cyclePatient(direction, source) {
    if (!this.is_patient_selection_open()) return false;

    const selected = this.patient_selection.cycle(direction);
    if (!selected) return false;

    this.patient_id = selected.id;
    this.append_sentry_message(
      `Patient selected by ${source}: pose id ${selected.id}`
    );
    return true;
  }

  /**
   * Start trigger from outside the frame loop (BLE remote START_STOP). Accepted once ready to start
   * with the selected participant in the start box, also while the raise-arm prompt is still
   * playing (it is cut short).
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if the trigger was accepted.
   */
  receiveStartTrigger(source) {
    // A remote start needs a participant selected with UP/DOWN (a hand raise or voice start doesn't)
    const patient = this.patient_selection.selected_patient();
    if (
      !patient ||
      this.current_state !== AssessmentStates.ready_to_start_test ||
      this.voice_command_received ||
      // update() ignores the first 2.5 s of this state
      Date.now() - this.two_second_start_time <= 2500 ||
      !MultiPoseUtility.is_someone_in_box(
        this.filter_poses_with_confident_ankles([patient]),
        this.patient_feet_box_x,
        this.patient_feet_box_y,
        this.camera
      )
    ) {
      return false;
    }
    // Same flag as voice START, so update() starts the test through the existing path
    this.voice_command_received = true;
    // Cut the prompt short: the remote may start while it is still playing
    this.audio_player.stop();
    if (this.enabled_voice_listener) {
      stopiOSVoiceListening();
      this.enabled_voice_listener = false;
    }
    this.append_sentry_message(`Start triggered by ${source}`);
    return true;
  }

  /**
   * Stop trigger from outside the frame loop (BLE remote START_STOP). Accepted only while the
   * test is running (start triggered, not yet finished or errored). Freezes update() so nothing
   * more is measured while the caller shows the exit choice.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if the trigger was accepted.
   */
  receiveStopTrigger(source) {
    if (
      (this.current_state !== AssessmentStates.start_gesture_detected &&
        this.current_state !== AssessmentStates.test_in_progress) ||
      ASSESSMENT_STATUSES.exit_assessment_modal ||
      ASSESSMENT_STATUSES.calibration_error_state
    ) {
      return false;
    }
    ASSESSMENT_STATUSES.exit_assessment_modal = true;
    this.append_sentry_message(`Assessment stopped by ${source}`);
    return true;
  }

  /**
   * NEXT trigger from outside the frame loop (BLE remote NEXT). Gait has a single phase, so before
   * the test starts NEXT leaves the assessment. Freezes update() while the caller navigates away.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {"exit"|null} What the caller should do, or null if NEXT doesn't apply now.
   */
  receiveNextTrigger(source) {
    const before_start = [
      AssessmentStates.assessment_started,
      AssessmentStates.pathway_calibration_start,
      AssessmentStates.pathway_calibration_audio_completed,
      AssessmentStates.ready_to_start_test,
    ].includes(this.current_state);
    if (
      !before_start ||
      this.voice_command_received ||
      this.hand_gesture_detected ||
      ASSESSMENT_STATUSES.exit_assessment_modal ||
      ASSESSMENT_STATUSES.calibration_error_state
    ) {
      return null;
    }
    ASSESSMENT_STATUSES.exit_assessment_modal = true;
    this.append_sentry_message(`Assessment exited by ${source} (NEXT before start)`);
    return "exit";
  }

  /**
   * If a user left the start box prematurely, trigger a return to start modal,
   * revert states, and log the event.
   */
  handleEveryoneBehindPoint() {
    this.is_everyone_behind_the_point_counter += 1;
    if (this.is_everyone_behind_the_point_counter >= 10) {
      document.getElementById("hdnRemoveGaitDontWalk")?.click();
      this.is_everyone_behind_the_point_counter = 0;
      if (this.enabled_voice_listener) {
        stopiOSVoiceListening();
        this.enabled_voice_listener = false;
        setTimeout(() => {
          this.audio_player.play(GaitAudios.return_to_start_area);
        }, 300);
      } else {
        this.audio_player.play(GaitAudios.return_to_start_area);
      }
      document.getElementById("hdnReturnToStartModalPopup").click();
      this.current_state = AssessmentStates.assessment_started;
      this.two_second_start_time = Date.now();
      this.append_sentry_message(
        "Someone started walking before hand raise detection"
      );
    }
  }

  /**
   * After detecting a start gesture, switch to single-pose estimation (e.g., BlazePose)
   * to isolate the main participant. This function also chooses which bounding box
   * to pass to the single-pose model for the best detection.
   * @param {Array} poses - The array of multi-pose keypoints from the current frame.
   */
  switch_to_blazepose(poses) {
    // A remote selection wins over the automatic pick (it already passed the start-area check)
    let patient =
      this.patient_selection.selected_patient() ||
      MultiPoseUtility.return_patient(
        poses,
        this.patient_feet_box_x,
        [
          this.patient_feet_box_y[0] -
            Math.abs(
              this.calibration.start_point_cv.y - this.patient_feet_box_y[1]
            ),
          this.calibration.start_point_cv.y,
        ],
        this.gesture_detector.gesture_issued_by,
        this.camera
      );
    if (!patient) {
      console.log(
        "Patient not found in the box - return_patient returning Null"
      );
    } else {
      let seed_rect = this.get_blazepose_bbox(patient);
      this.patient_pose = patient;

      console.log(`Seed Rect : ${JSON.stringify(seed_rect)}`);
      this.switch_to_single_pose_callback(seed_rect);

      this.append_sentry_message(
        "Switching to blaze pose with seed rect: " + JSON.stringify(seed_rect)
      );
    }
  }

  /**
   * Constructs a bounding box (seed_rect) for BlazePose from the current multi-pose detection.
   * @param {object} patient - The main patient pose object, including keypoints and bounding box.
   * @returns {object} The bounding box rectangle in normalized coordinates for BlazePose input.
   */
  get_blazepose_bbox(patient) {
    let left_hip = patient.keypoints[11];
    let right_hip = patient.keypoints[12];
    let spine_base = {
      x: (left_hip.x + right_hip.x) / 2,
      y: (left_hip.y + right_hip.y) / 2,
    };

    let bbox = patient.box;
    let box_size = 2 * Math.ceil(bbox.yMax * this.options.sh - spine_base.y);

    console.log("Seed box", bbox, spine_base, box_size);
    this.append_sentry_message(
      "Seed box: " +
        JSON.stringify(bbox) +
        " spine base: " +
        JSON.stringify(spine_base) +
        " box size: " +
        box_size
    );

    let seed_rect = {
      xCenter: spine_base.x / this.camera.video.videoWidth,
      yCenter: spine_base.y / this.camera.video.videoHeight,
      width: box_size / this.camera.video.videoWidth,
      height: box_size / this.camera.video.videoHeight,
      rotation: 0.0,
    };
    return seed_rect;
  }

  /**
   * Creates a downloadable CSV file of the stored ankle and foot positions (Bird's Eye View).
   */
  download_ankle_locations() {
    let csvContent = "data:text/csv;charset=utf-8,";
    this.ankle_locations_bev.forEach(function (rowArray) {
      let row = rowArray.join(",");
      csvContent += row + "\r";
    });
    var encodedUri = encodeURI(csvContent);
    var link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "scase5_feet_n_ankle_locations.csv");
    document.body.appendChild(link);

    link.click(); // Download the CSV file
  }

  /**
   * Records the ankle positions of detected persons in Bird's Eye View for subsequent gait analysis.
   * @param {Array} poses - Array of pose objects from the current frame.
   */
  record_ankle_locations_BEV(poses) {
    if (this.persons_detected(poses)) {
      for (let i = 0; i < poses.length; i++) {
        let left_ankle = poses[i].keypoints[this.LEFT_ANKLE_IDX];
        let right_ankle = poses[i].keypoints[this.RIGHT_ANKLE_IDX];
        let left_foot = poses[i].keypoints[this.LEFT_FOOT_IDX];
        let right_foot = poses[i].keypoints[this.RIGHT_FOOT_IDX];
        let feet = this.get_ankle_locations_in_birds_eye_view(poses[i]);
        let feet_data = new FeetData(feet.left, feet.right);

        // Save both the Bird's Eye View and camera coordinate data
        this.feet_locations.push(feet_data);
        this.feet_locations_cam.push(new FeetData(left_foot, right_foot));
        this.ankle_locations_bev.push([
          Date.now(),
          feet.left.x,
          feet.left.y,
          feet.right.x,
          feet.right.y,
          left_foot.x,
          left_foot.y,
          right_foot.x,
          right_foot.y,
          left_ankle.x,
          left_ankle.y,
          right_ankle.x,
          right_ankle.y,
          left_foot.score,
          right_foot.score,
          left_ankle.score,
          right_ankle.score,
        ]);
      }
    }
  }

  /**
   * Detects if a second person has crossed or is crossing in front of/behind the patient.
   * Simplistic check based on shoulder distances and movement over frames.
   * @param {Array} poses - Detected multi-person pose array.
   * @returns {boolean} True if second person crossing is detected, false otherwise.
   */
  second_person_crossing_detection(poses) {
    if (this.persons_detected(poses)) {
      for (let i = 0; i < poses.length; i++) {
        let left_shoulder_value = poses[i].keypoints[this.LEFT_SHOULDER_IDX];
        let right_shoulder_value = poses[i].keypoints[this.RIGHT_SHOULDER_IDX];
        this.shoulder_differernce = Math.abs(
          left_shoulder_value.x - right_shoulder_value.x
        );
        let shoulder_mean =
          (left_shoulder_value.x + right_shoulder_value.x) / 2;
        if (this.prev_index !== 0) {
          if (
            this.shoulder_differernce < this.prev_index * 0.6 ||
            shoulder_mean < this.right_shoulder ||
            shoulder_mean > this.left_boundary
          ) {
            console.log(
              "assessment-error: shoulder width reduced or moved. ",
              this.shoulder_differernce < this.prev_index * 0.6,
              " ",
              this.right_shoulder,
              " ",
              this.left_boundary,
              " ",
              shoulder_mean
            );
            return true;
          }
        }
        this.prev_index = this.shoulder_differernce;
        this.right_shoulder = right_shoulder_value.x;
        this.left_boundary = left_shoulder_value.x;
      }
      return false;
    }
  }

  /**
   * Filters poses to only those where both ankle keypoints have sufficient
   * confidence. Prevents half-pose detections (e.g., knees misidentified as
   * ankles) from being used in bounding box checks.
   * @param {Array} poses - Array of detected poses.
   * @returns {Array} Poses with confident ankle keypoints only.
   */
  filter_poses_with_confident_ankles(poses) {
    const ANKLE_CONFIDENCE_THRESHOLD = 0.25;
    if (!poses) return [];
    return poses.filter((person) => {
      let left_ankle = person.keypoints?.[15];
      let right_ankle = person.keypoints?.[16];
      return (
        left_ankle?.score >= ANKLE_CONFIDENCE_THRESHOLD &&
        right_ankle?.score >= ANKLE_CONFIDENCE_THRESHOLD
      );
    });
  }

  /**
   * Calculates the symmetry index between left and right limb metrics.
   * @param {number} left - Left limb metric (e.g., step length).
   * @param {number} right - Right limb metric (e.g., step length).
   * @returns {number} Symmetry Index (SI) as a percentage.
   */
  calculateSymmetryIndex(left, right) {
    if (left + right === 0) return 0;
    return (Math.abs(right - left) / ((right + left) / 2)) * 100;
  }

  /**
   * Builds the final gait assessment response object.
   * @param {number} gait_speed_wrong - A fallback speed calculation if the more precise method is not available.
   * @param {number} distance - The distance walked in meters.
   * @param {number} gait_time - The time taken in seconds.
   * @returns {object} The structured gait assessment response.
   */
  build_gait_response(gait_speed_wrong, distance, gait_time) {
    let {
      step_count,
      gait_speed,
      left_step_length,
      right_step_length,
      avg_step_length,
      // stride_length,
      step_timestamps,
    } = this.step_detector.count_steps(
      this.feet_locations,
      this.feet_locations_cam,
      this.calibration.depthData
    );
    // Step Time Variability: standard deviation of step intervals (ms)
    let step_time_variability = null;
    if (Array.isArray(step_timestamps) && step_timestamps.length > 1) {
      let intervals = [];
      for (let i = 1; i < step_timestamps.length; i++) {
        intervals.push(step_timestamps[i] - step_timestamps[i - 1]);
      }
      if (intervals.length > 0) {
        let mean = intervals.reduce((a, b) => a + b, 0) / intervals.length;
        let variance =
          intervals.reduce((a, b) => a + Math.pow(b - mean, 2), 0) /
          intervals.length;
        step_time_variability = Math.sqrt(variance);
      }
    }
    const gait_speed_final = gait_speed ?? gait_speed_wrong;

    // Calculate symmetry index using step length if available
    let symmetry_index = null;
    if (
      typeof left_step_length === "number" &&
      typeof right_step_length === "number"
    ) {
      symmetry_index = this.calculateSymmetryIndex(
        left_step_length,
        right_step_length
      );
    }

    // Suspicious step counts trigger alert emails
    if (step_count > 25 || step_count <= 2) {
      this.email_generation_stepcount(step_count);
      this.append_sentry_message(
        `Unusual Step Count Detected. Detected steps: ${step_count}`
      );
    }
    // Suspicious gait speed triggers alert emails
    if (gait_speed > 2) {
      this.email_generation_gaitspeed(gait_speed);
      this.append_sentry_message(`Unusual Gait Speed: ${gait_speed}`);
    }

    return {
      test_type: "gait",
      measures: {
        score: {
          fall_risk: calculateFallRisk(
            gait_speed_final,
            getPatientAge()
          ).toFixed(2),
          gait_speed: gait_speed_final,
          distance: distance,
          ambulation_time: gait_time,
          velocity: gait_speed * 100,
          mean_normalized_velocity: 175.18,
          number_of_steps: step_count,
          cadence: 60 * (step_count / gait_time),
          symmetry_index: symmetry_index,
          left_step_length: left_step_length,
          right_step_length: right_step_length,
          avg_step_length: avg_step_length,
          step_time_variability: step_time_variability,
        },
        used_assistive_device: "False",
      },
      start_time: this.start_time.toString(),
      end_time: this.end_time.toString(),
    };
  }

  /**
   * Wrapper for the calibration function to get a point in Bird's Eye View.
   * @param {object} point - Keypoint with x,y fields in camera coordinates.
   * @returns {object} Transformed point in Bird's Eye View coordinates.
   */
  get_birds_eye_view_point(point) {
    return this.calibration.get_birds_eye_view_point(point);
  }

  /**
   * Checks if at least one person pose is detected.
   * @param {Array} poses - Array of pose objects from the current frame.
   * @returns {boolean} True if poses are present, otherwise false.
   */
  persons_detected(poses) {
    if (!poses || poses.length === 0) {
      return false;
    }
    return true;
  }

  /**
   * Retrieves the ankle locations of a single pose in Bird's Eye View.
   * @param {object} pose - A single pose object containing keypoints.
   * @returns {{left: object, right: object}} The left and right foot coordinates in Bird's Eye View.
   */
  get_ankle_locations_in_birds_eye_view(pose) {
    let left_ankle = pose.keypoints[this.LEFT_FOOT_IDX];
    let right_ankle = pose.keypoints[this.RIGHT_FOOT_IDX];
    let warped_left_ankle = this.get_birds_eye_view_point(left_ankle);
    let warped_right_ankle = this.get_birds_eye_view_point(right_ankle);

    return {
      left: warped_left_ankle,
      right: warped_right_ankle,
    };
  }

  /**
   * Sets references for the patient's shoulder region, used to detect crossing or interference.
   * @param {Array} poses - Array of detected poses. Should primarily contain the patient's pose.
   */
  setting_patient_region(poses) {
    this.left_shoulder_bound = poses[0].keypoints[this.LEFT_SHOULDER_IDX];
    this.right_shoulder_bound = poses[0].keypoints[this.RIGHT_SHOULDER_IDX];
    this.shoulder_diff = Math.abs(
      this.left_shoulder_bound.x - this.right_shoulder_bound.x
    );
    this.left_shoulder_bound.x =
      this.left_shoulder_bound.x + this.shoulder_diff;
    this.right_shoulder_bound.x =
      this.right_shoulder_bound.x - this.shoulder_diff;

    if (this.left_shoulder_bound.x > this.videoWidth) {
      this.left_shoulder_bound.x = this.videoWidth;
    }
    if (this.right_shoulder_bound.x < 0) {
      this.right_shoulder_bound.x = 0;
    }
  }

  /**
   * @returns {boolean} True if the finish line has been crossed, false otherwise.
   */
  is_finish_line_crossed() {
    return this.finish_line_crossed;
  }

  /**
   * @returns {boolean} True if the start line has been crossed, false otherwise.
   */
  is_start_line_crossed() {
    return this.start_line_crossed;
  }

  /**
   * Detect if a non-patient crossing scenario might have occurred based on sudden large movements in Bird's Eye View.
   * @param {Array} poses - Array of detected poses.
   * @returns {boolean} True if crossing is detected, otherwise false.
   */
  bird_eye_view_patient_crossing(poses) {
    if (this.persons_detected(poses)) {
      for (let i = 0; i < poses.length; i++) {
        let left_ankle = poses[i].keypoints[this.LEFT_ANKLE_IDX];
        let right_ankle = poses[i].keypoints[this.RIGHT_ANKLE_IDX];

        let jump_limit =
          Math.abs(
            this.calibration.stop_point.y - this.calibration.start_point.y
          ) * 0.65;

        let warped_left_ankle =
          this.calibration.get_birds_eye_view_point(left_ankle);
        let warped_right_ankle =
          this.calibration.get_birds_eye_view_point(right_ankle);

        // Compare with previous frame positions
        if (this.prev_left_ankle_value !== 0) {
          if (
            Math.abs(this.prev_left_ankle_value - warped_left_ankle.y) >=
              jump_limit ||
            Math.abs(this.prev_right_ankle_value - warped_right_ankle.y) >=
              jump_limit
          ) {
            console.log(
              "assessment-error: bird eye view - patient crossing alert",
              Math.abs(this.prev_left_ankle_value - warped_left_ankle.y),
              " ",
              Math.abs(this.prev_right_ankle_value - warped_right_ankle.y),
              " ",
              jump_limit
            );
            return true;
          }
        }
        if (warped_left_ankle.y > 0 && warped_right_ankle.y > 0) {
          this.prev_left_ankle_value = warped_left_ankle.y;
          this.prev_right_ankle_value = warped_right_ankle.y;
        }
      }
    }
    return false;
  }

  /**
   * Checks if the patient is near the stop line by comparing the ankles in Bird's Eye View
   * to the stop point within a small threshold.
   * @param {Array} poses - Array of detected poses.
   */
  patient_near_stopline(poses) {
    if (this.persons_detected(poses)) {
      for (let i = 0; i < poses.length; i++) {
        let left_ankle = poses[i].keypoints[this.LEFT_ANKLE_IDX];
        let right_ankle = poses[i].keypoints[this.RIGHT_ANKLE_IDX];
        let limit =
          Math.abs(
            this.calibration.stop_point.y - this.calibration.start_point.y
          ) * 0.15;
        let warped_left_ankle =
          this.calibration.get_birds_eye_view_point(left_ankle);
        let warped_right_ankle =
          this.calibration.get_birds_eye_view_point(right_ankle);

        // If close to the stop line, set a flag
        if (
          Math.abs(this.calibration.stop_point.y - warped_left_ankle.y) <=
            limit ||
          Math.abs(this.calibration.stop_point.y - warped_right_ankle.y) <=
            limit
        ) {
          console.log("Patient is almost at the stop line");
          this.isPatientNearStopLine = true;
        }
      }
    }
  }

  /**
   * Appends a message to the Sentry log with a timestamp.
   * @param {string} message - The message to log.
   */
  append_sentry_message(message) {
    let event = new Date();
    ASSESSMENT_STATUSES.sentry_message =
      ASSESSMENT_STATUSES.sentry_message +
      "\n" +
      event.toISOString() +
      ": " +
      message;

    // Additional time logging for calibration or gestures
    if (
      message === "Calibration Started" ||
      message === "Hand Raised Modal is displayed."
    ) {
      this.sentry_start_time = Date.now();
    } else if (message === "Calibration complete!") {
      let diff = (Date.now() - this.sentry_start_time) / 1000;
      ASSESSMENT_STATUSES.sentry_message =
        ASSESSMENT_STATUSES.sentry_message +
        ", Paper detection Calibration time: " +
        diff +
        " seconds";
    } else if (message === "Start (Hand raised) gesture issued!") {
      let diff = (Date.now() - this.sentry_start_time) / 1000;
      ASSESSMENT_STATUSES.sentry_message =
        ASSESSMENT_STATUSES.sentry_message +
        ", Hand Raise gesture detection time: " +
        diff +
        " seconds";
    }
  }

  /**
   * Checks if any person pose is within a central path region, indicating the path is blocked.
   * @param {Array} poses - Array of multi-person pose objects.
   * @returns {boolean} True if the path is clear (no one inside it), false if blocked.
   */
  path_checking(poses) {
    if (!poses || !Array.isArray(poses)) {
      // If poses isn't valid, assume the path is clear
      return false;
    }

    // Define the bounding box for the path
    const xMin = this.options.sw / 2;
    const xMax = this.options.sw / 2 + 250;
    const yMin = this.calibration.start_point_extended_cv.y;
    const yMax = this.camera.video.videoHeight;

    let fillxMin = this.camera.scale_to_fit({ x: xMin, y: 0 }).x;
    let fillyMin = this.camera.scale_to_fit({ x: 0, y: yMin }).y;
    let fillxMax = this.camera.scale_to_fit({ x: xMax, y: 0 }).x;
    let fillyMax = this.camera.scale_to_fit({ x: 0, y: yMax }).y;

    // For each person, check if any keypoint is in the path bounding box
    for (let person of poses) {
      console.log("person detected");
      if (person.score > 0.25) {
        for (let kp of person.keypoints) {
          if (kp.score > 0.25) {
            let scaledX = this.camera.scale_to_fit({ x: kp.x, y: 0 }).x;
            let scaledY = this.camera.scale_to_fit({ x: 0, y: kp.y }).y;
            if (
              scaledX >= fillxMin &&
              scaledX <= fillxMax &&
              scaledY >= fillyMin &&
              scaledY <= fillyMax
            ) {
              console.log("found another person in the path");
              return false;
            }
          }
        }
      }
    }

    // No one is in the path
    return true;
  }

  /**
   * Based on a stored angle value, sets an "extended start point" and associated line
   * for path checking or bounding box placement.
   */
  get_start_extended_points() {
    const y_axis = parseInt(ASSESSMENT_STATUSES.current_angle);

    const angleToYMap = {
      88: 1085,
      89: 1095,
      90: 1105,
      91: 1120,
      92: 1130,
    };

    const y_extended = angleToYMap[y_axis];
    this.calibration.start_point_extended_cv = new cv.Point(
      this.options.sw / 2,
      y_extended
    );
    this.calibration.start_line_extended_cv = {
      x1: Math.min(this.calibration.start_point_extended_cv.x - 50, 0),
      y1: this.calibration.start_point_extended_cv.y,
      x2: Math.max(
        this.calibration.start_point_extended_cv.x + 50,
        this.options.sw
      ),
      y2: this.calibration.start_point_extended_cv.y,
    };
  }

  /**
   * Draws a "path clearance" box over the canvas to visualize the region we check for blockages.
   * Animates a color fill from bottom to top.
   */
  draw_PathClearanceBox() {
    const c = document.getElementById("output");
    const ctx = c.getContext("2d");
    ctx.lineWidth = 3;

    // Create a vertical gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, c.height);
    gradient.addColorStop(0, "rgba(0, 255, 255, 0.2)");
    gradient.addColorStop(1, "rgba(255, 0, 255, 0.2)");

    ctx.fillStyle = gradient;
    ctx.strokeStyle = "magenta";

    const xMin = this.options.sw / 2;
    const xMax = this.options.sw / 2 + 250;
    const yMin = this.calibration.start_point_extended_cv.y;
    const yMax = this.camera.video.videoHeight;

    let fillxMin = this.camera.scale_to_fit({ x: xMin, y: 0 }).x;
    let fillyMin = this.camera.scale_to_fit({ x: 0, y: yMin }).y;
    let fillxMax = this.camera.scale_to_fit({ x: xMax, y: 0 }).x;
    let fillyMax = this.camera.scale_to_fit({ x: 0, y: yMax }).y;

    const ctx2 = c.getContext("2d");
    ctx2.beginPath();
    ctx2.strokeStyle = "yellow";
    ctx2.lineWidth = 3;
    ctx2.fillStyle = "rgba(255,255,0,0.2)";
    ctx2.fillRect(fillxMin, fillyMin, fillxMax - fillxMin, fillyMax - fillyMin);
    ctx2.rect(fillxMin, fillyMin, fillxMax - fillxMin, fillyMax - fillyMin);
    ctx2.stroke();
    ctx2.closePath();

    if (this.current_y < fillyMin) {
      this.current_y = fillyMax;
      this.old_y = fillyMax;
    }

    // Basic animation that moves upward
    const animate = () => {
      if (this.current_y < fillyMax) {
        ctx.clearRect(fillxMin, this.old_y - 5, fillxMax - fillxMin, 10);
      }
      ctx.beginPath();
      ctx.fillRect(
        fillxMin,
        this.current_y,
        fillxMax - fillxMin,
        fillyMax - this.current_y
      );
      ctx.rect(
        fillxMin,
        this.current_y,
        fillxMax - fillxMin,
        fillyMax - this.current_y
      );
      ctx.stroke();
      ctx.closePath();

      this.old_y = this.current_y;
      if (this.current_y > fillyMin) {
        this.current_y -= 2;
        requestAnimationFrame(animate);
      }
    };
    animate();
  }

  /**
   * Captures a frame (PNG) from the video, uploads it to S3,
   * then calls an external API to generate depth data. If it fails multiple times,
   * it raises an error, cancels the assessment, and plays an interruption audio.
   */
  async uploadImageToS3AndCallAPI() {
    try {
      let PatientId = localStorage.getItem("patientServerID");
      let image_name = `images/scene_depth_input_${PatientId}.jpg`;
      const s3Bucket = S3_BUCKET;
      const s3Key = `depths/scene_depth_${PatientId}.npy`;

      // Capture current frame to offscreen canvas
      this.offscreen_canvas.width = this.camera.video.videoWidth;
      this.offscreen_canvas.height = this.camera.video.videoHeight;
      this.offscreen_canvas_ctx.drawImage(
        this.camera.video,
        0,
        0,
        this.camera.video.videoWidth,
        this.camera.video.videoHeight
      );

      const imageUrl = this.offscreen_canvas.toDataURL("image/jpeg", 0.9);

      // Convert base64 to binary
      const base64Data = imageUrl.replace(/^data:image\/jpeg;base64,/, "");
      const binaryData = atob(base64Data);
      const binaryBuffer = new Uint8Array(binaryData.length);

      for (let i = 0; i < binaryData.length; i++) {
        binaryBuffer[i] = binaryData.charCodeAt(i);
      }

      // Upload the screenshot to S3
      const params = {
        Bucket: s3Bucket,
        Key: image_name,
        Body: binaryBuffer,
        ContentEncoding: "base64",
        ContentType: "image/jpeg",
      };
      await s3Client.send(new PutObjectCommand(params));
      console.log("Successfully uploaded data to S3");
      this.append_sentry_message("Successfully uploaded data to S3");

      const apiURL = API_URL;
      const postData = {
        image_path: image_name,
        model_selection: "vit-large",
        PatientId: PatientId,
        fx: 740,
        fy: 740,
      };

      // Retry logic for external API call
      let attempts = 3;
      while (attempts > 0) {
        try {
          const controller = new AbortController();
          const id = setTimeout(() => controller.abort(), 15000); // 15s timeout

          const response = await fetch(apiURL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(postData),
            signal: controller.signal,
          });

          clearTimeout(id);

          if (!response.ok) {
            this.append_sentry_message(
              `HTTP error! Status: ${response.status}`
            );
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          console.log("success from API");
          this.append_sentry_message("API call successful");

          // Load the generated .npy file from S3 into calibration
          this.is_uploaded = await this.calibration.get_depth_data(
            s3Bucket,
            s3Key
          );
          break;
        } catch (error) {
          console.error("Error in API call:", error);
          this.append_sentry_message("Error in API call");
          attempts--;
          if (attempts === 0) {
            console.error("API request failed after multiple attempts.");
            this.append_sentry_message(
              "API request failed after multiple attempts."
            );
            // Stop voice listening on API failure
            if (this.enabled_voice_listener) {
              console.log("🔊 Stopping voice listening due to API failure");
              stopiOSVoiceListening();
              this.enabled_voice_listener = false;
            }
            this.current_state = AssessmentStates.test_complete;
            document.getElementById("hdnRemoveGaitDontWalk")?.click();
            document.getElementById("hdnAssessementErrorPopup").click();
            ASSESSMENT_STATUSES.assessment_Completed = true;
            localStorage.removeItem("modalDisplayed");
            this.audio_player.play(GaitAudios.assessment_interrupted);
            return;
          } else {
            console.log(`Retrying... attempts left: ${attempts}`);
            this.append_sentry_message(
              `Retrying... attempts left: ${attempts}`
            );
          }
        }
      }
    } catch (err) {
      console.log("Error uploading data: ", err);
      this.append_sentry_message("Error uploading data to S3 bucket");
      // Stop voice listening on S3 upload error
      if (this.enabled_voice_listener) {
        console.log("🔊 Stopping voice listening due to S3 upload error");
        stopiOSVoiceListening();
        this.enabled_voice_listener = false;
      }
      this.current_state = AssessmentStates.test_complete;
      document.getElementById("hdnRemoveGaitDontWalk")?.click();
      document.getElementById("hdnAssessementErrorPopup").click();
      ASSESSMENT_STATUSES.assessment_Completed = true;
      localStorage.removeItem("modalDisplayed");
      this.audio_player.play(GaitAudios.assessment_interrupted);
      return;
    }
  }

  /**
   * Draws a rectangular box on the canvas near the "start line extended" position
   * to indicate where the patient should stand initially.
   * @param {Array} poses - Array of detected poses, used to check if the patient is within the box.
   *
   * TODO: Refactor into a shared BoundingBoxRenderer utility class.
   * The drawing logic (roundRect, fill color, shoe print image, dotted border) is
   * duplicated across gait.js, tug.js, mCTSIB.js, and sitStandProcessing.js.
   * Each assessment calculates box coordinates differently (gait uses start_line_extended_cv,
   * TUG uses create_patient_box_at_distance with 3ft/13ft, mCTSIB uses 6ft, sitStandProcessing
   * uses fixed margins), but the rendering is identical. A shared class with a
   * draw(ctx, x, y, width, height, isPatientInBox) method would eliminate this duplication.
   */
  draw_PatientFeetBox(poses) {
    var c = document.getElementById("output");
    let rect_start_x = Math.floor(c.width * this.side_margin) + 50;
    let scale_to_fit_the_point = this.camera.scale_to_fit_the_point(
      this.calibration.start_line_extended_cv.x2 + 100,
      this.calibration.start_line_extended_cv.y2
    );
    let rect_start_y = Math.floor(scale_to_fit_the_point.y);
    let rect_width = c.width * (1 - 2 * this.side_margin) + 100;
    let rect_height = this.camera.scale_to_fit_the_point(
      this.calibration.start_line_extended_cv.x2 + 100,
      this.calibration.start_line_extended_cv.y2 - FEET_BOX_LENGTH
    ).y;
    rect_height = -Math.abs(scale_to_fit_the_point.y - rect_height);

    this.patient_feet_box_x[0] = rect_start_x;
    this.patient_feet_box_x[1] = rect_start_x + rect_width;
    this.patient_feet_box_y[0] =
      this.calibration.start_line_extended_cv.y2 - FEET_BOX_LENGTH;
    this.patient_feet_box_y[1] = this.calibration.start_line_extended_cv.y2;

    this.ctx.beginPath();
    this.ctx.lineWidth = 5;

    // Buffer variable -> 30% above & below the box's middle point
    let patient_inner_buffer = Math.abs(
      (this.patient_feet_box_y[0] - this.patient_feet_box_y[1]) * 0.2
    );

    let patient_in_box = MultiPoseUtility.is_someone_in_box(
      this.filter_poses_with_confident_ankles(poses),
      this.patient_feet_box_x,
      [
        this.patient_feet_box_y[0] + patient_inner_buffer,
        this.patient_feet_box_y[1] - patient_inner_buffer,
      ],
      this.camera
    );

    // Fill color indicates whether someone is present in the box
    if (patient_in_box) {
      this.ctx.fillStyle = "rgba(0,128,0,0.75)";
    } else {
      this.ctx.fillStyle = "rgba(150,75,0,0.75)";
    }

    this.ctx.roundRect(rect_start_x, rect_start_y, rect_width, rect_height, 20);
    this.ctx.fill();

    // Draw shoe print image centered in the box
    if (this.shoe_print_loaded) {
      const imgAspect = this.shoe_print_img.width / this.shoe_print_img.height;
      const boxAbsHeight = Math.abs(rect_height);
      const imgHeight = boxAbsHeight * 0.8;
      const imgWidth = imgHeight * imgAspect;
      const imgX = rect_start_x + (rect_width - imgWidth) / 2;
      const imgY = rect_start_y + (rect_height - imgHeight) / 2;
      this.ctx.globalAlpha = 0.85;
      this.ctx.drawImage(this.shoe_print_img, imgX, imgY, imgWidth, imgHeight);
      this.ctx.globalAlpha = 1.0;
    }

    // Dotted border lines
    this.ctx.lineWidth = 5;
    this.ctx.setLineDash([10, 5]);
    this.ctx.strokeStyle = "rgba(0,0,0,0.75)";
    this.ctx.stroke();
    this.ctx.setLineDash([]);
  }

  /**
   * Checks for sudden, large changes in shoulder positions (indicative of flicker or detection error).
   * Logs an alert if values deviate drastically between frames.
   * @param {Array} poses - Array of detected poses in the current frame.
   */
  flickerDetection(poses) {
    if (this.persons_detected(poses)) {
      for (let i = 0; i < poses.length; i++) {
        let left_shoulder = poses[i].keypoints[11];
        let right_shoulder = poses[i].keypoints[12];

        let warped_left_shoulder = left_shoulder;
        let warped_right_shoulder = right_shoulder;

        if (
          this.prev_right_shoulder_value.y != null &&
          this.prev_right_shoulder_value.x != null &&
          this.prev_left_shoulder_value.y != null &&
          this.prev_left_shoulder_value.x != null
        ) {
          let right_magnitude = Math.sqrt(
            Math.pow(this.prev_right_shoulder_value.y - right_shoulder.y, 2) +
              Math.pow(this.prev_right_shoulder_value.x - right_shoulder.x, 2)
          );
          let left_magnitude = Math.sqrt(
            Math.pow(this.prev_left_shoulder_value.y - left_shoulder.y, 2) +
              Math.pow(this.prev_left_shoulder_value.x - left_shoulder.x, 2)
          );

          // If magnitude is large, consider it a flicker
          if (right_magnitude >= 300 || left_magnitude >= 300) {
            console.log("Sudden Change in the Pose values are observed.");
            this.append_sentry_message(
              "Sudden Change in the Pose values are observed."
            );
            this.flickerStatus = true;
          }
        }

        // Store current frame's shoulder for next iteration
        this.prev_right_shoulder_value.x = warped_right_shoulder.x;
        this.prev_right_shoulder_value.y = warped_right_shoulder.y;
        this.prev_left_shoulder_value.x = warped_left_shoulder.x;
        this.prev_left_shoulder_value.y = warped_left_shoulder.y;
      }
    }
  }

  /**
   * Sends an email alert when a suspiciously high or low step count is detected.
   * @param {number} step_count - The number of steps detected by the step detector.
   */
  email_generation_stepcount = async (step_count) => {
    console.log("step counts:", step_count);
    let subject = this.sentry_prefix + ": Steps count looks unusuall.";
    let body = this.sentry_prefix + "\n";
    body = body + "Steps count: " + step_count + "\n";

    try {
      await SendExceptionEmail({
        subject: subject,
        body: body,
      })
        .then(async function (response) {
          console.log("Zendesk Email: ", response.data);
        })
        .catch((error) => {
          console.error(error);
        });
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Sends an email alert when a suspiciously high gait speed is detected.
   * @param {number} gait_speed - The detected gait speed in m/s.
   */
  email_generation_gaitspeed = async (gait_speed) => {
    console.log("gait speed:", gait_speed);
    let subject = this.sentry_prefix + ": Gait speed looks unusuall.";
    let body = this.sentry_prefix + "\n";
    body = body + "Gait speed: " + gait_speed + "\n";

    try {
      await SendExceptionEmail({
        subject: subject,
        body: body,
      })
        .then(async function (response) {
          console.log("Zendesk Email: ", response.data);
        })
        .catch((error) => {
          console.error(error);
        });
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Returns true when cropped frames should be sent to MoveNet.
   * This is active when patient feet box is known and hand gesture is not yet detected.
   */
  shouldUseCroppedFrames() {
    return (
      this.patient_feet_box_x.length >= 2 &&
      this.patient_feet_box_y.length >= 2 &&
      !this.hand_gesture_detected &&
      this.current_state !== AssessmentStates.test_complete
    );
  }

  /**
   * Computes a square crop rect in video coordinates for hand raise detection.
   * The crop is horizontally centered on the feet box and extends upward from
   * the bottom of the feet box (plus a small buffer).
   * @param {number} cropSize - The side length of the square crop (e.g. 640).
   * @returns {{ cropX: number, cropY: number, cropSize: number } | null}
   */
  getHandRaiseCropRect(cropSize = 640) {
    if (
      this.patient_feet_box_x.length < 2 ||
      this.patient_feet_box_y.length < 2
    ) {
      return null;
    }

    const videoWidth = this.camera.video.videoWidth; // 1080
    const videoHeight = this.camera.video.videoHeight; // 1920

    // patient_feet_box_x is in canvas space -> convert center to video space
    const canvasWidth = this.camera.canvas_width;
    const boxCenterXCanvas =
      (this.patient_feet_box_x[0] + this.patient_feet_box_x[1]) / 2;
    const centerX = (boxCenterXCanvas / canvasWidth) * videoWidth;

    // patient_feet_box_y is in video coordinates already
    // Add buffer below feet box bottom, then extend upward
    const bottomBuffer = 50;
    const cropBottom = this.patient_feet_box_y[1] + bottomBuffer;

    // Calculate crop rectangle (extends upward from bottom)
    let cropX = Math.round(centerX - cropSize / 2);
    let cropY = cropBottom - cropSize;

    // Clamp to video bounds
    cropX = Math.max(0, Math.min(cropX, videoWidth - cropSize));
    cropY = Math.max(0, cropY);
    // Ensure bottom doesn't exceed frame
    if (cropY + cropSize > videoHeight) {
      cropY = videoHeight - cropSize;
    }

    return { cropX, cropY, cropSize };
  }
}

/**
 * Initializes Sentry logging for Gait-based assessments.
 */
export async function gaitSentryLogging() {
  SentryLogging("Gait");
}
